import { useState } from "react";
import { Loader2 } from "lucide-react";
import { useTranslations } from "use-intl";
import { bankgiroInputFeedback, maskBankgiroInput } from "@financialharmony/tax-se";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { userFacingErrorMessage } from '@/utils/userFacingError';

/**
 * The consultant app's inline add/change-bankgiro form (FIN-818), used on Create Invoice and in
 * settings. The number alone: which account it pays into is never asked (FIN-1172).
 */
export function BankgiroEntryForm({ initialValue = "", onSave, onCancel }: {
  initialValue?: string;
  onSave: (typed: string) => Promise<unknown>;
  onCancel: () => void;
}) {
  const t = useTranslations("consultantInvoiceCreate.bankgiroForm");
  const [typed, setTyped] = useState(initialValue);
  const [blurred, setBlurred] = useState(false);
  const [saving, setSaving] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);
  const feedback = bankgiroInputFeedback(typed, { blurred });

  const save = async () => {
    setSaving(true);
    setFailure(null);
    try {
      await onSave(typed);
    } catch (err) {
      setFailure(userFacingErrorMessage(err, t("saveFailed")));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-2 space-y-3 rounded-md border bg-muted/40 p-3">
      <div className="space-y-1.5">
        <Label htmlFor="bankgiro-number">{t("number")}</Label>
        <Input
          id="bankgiro-number"
          aria-label={t("numberLabel")}
          inputMode="numeric"
          placeholder={t("placeholder")}
          value={typed}
          disabled={saving}
          onChange={(e) => {
            setTyped(maskBankgiroInput(e.target.value, typed));
            setBlurred(false);
          }}
          onBlur={() => setBlurred(true)}
        />
        {feedback.error && <p className="text-xs text-destructive">{t("invalid")}</p>}
        {!feedback.error && feedback.formatted && (
          <p className="text-xs text-muted-foreground">
            {t.rich("formatted", {
              formatted: feedback.formatted,
              number: (chunks) => <span className="font-medium text-foreground">{chunks}</span>,
            })}
          </p>
        )}
        {!feedback.error && !feedback.formatted && <p className="text-xs text-muted-foreground">{t("why")}</p>}
      </div>
      {failure && <p role="alert" className="text-sm text-destructive">{failure}</p>}
      <div className="flex gap-2">
        <Button type="button" size="sm" disabled={!feedback.formatted || saving} onClick={save}>
          {saving && <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" />}
          {saving ? t("saving") : t("save")}
        </Button>
        <Button type="button" size="sm" variant="ghost" disabled={saving} onClick={onCancel}>{t("cancel")}</Button>
      </div>
    </div>
  );
}
